import { useState, type ReactNode } from 'react'

/**
 * One collapsible section of the backoffice.
 *
 * Shared by every panel so the screen's shell owns no panel's state and every panel gets the same
 * header treatment: a title, an optional status line, and a body that can be folded away. An
 * operator on a matchday wants the live matches open and everything else shut.
 */
export default function BackofficePanel({
  title,
  subtitle,
  badge,
  defaultOpen = true,
  actions,
  children,
}: {
  title: string
  /** One line under the title — usually the panel's own connection or freshness state. */
  subtitle?: ReactNode
  /** A short status word rendered beside the title: "live", "3 alerts", "paused". */
  badge?: ReactNode
  defaultOpen?: boolean
  /** Controls that belong to the panel's header rather than its body. */
  actions?: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <section className="bo-panel">
      <header className="bo-panel__header">
        <button
          type="button"
          className="bo-panel__toggle"
          aria-expanded={open}
          onClick={() => setOpen((previous) => !previous)}
        >
          <span className="bo-panel__chevron" aria-hidden="true">
            {open ? '▾' : '▸'}
          </span>
          <span className="bo-panel__title">{title}</span>
          {badge !== undefined && <span className="bo-panel__badge">{badge}</span>}
        </button>
        {actions && <div className="bo-panel__actions">{actions}</div>}
      </header>
      {subtitle !== undefined && <p className="bo-panel__subtitle">{subtitle}</p>}
      {open && <div className="bo-panel__body">{children}</div>}
    </section>
  )
}

/**
 * A panel's own error, rendered in place.
 *
 * Every panel fetches its own data and renders its own error — a failing health endpoint must not
 * blank the live-matches panel next to it.
 */
export function PanelError({ error }: { error: string }) {
  return <p className="bo-error">could not load: {error}</p>
}

/** A panel that cannot function in this mode says why, in one line (the epic's constraint 6). */
export function PanelNote({ children }: { children: ReactNode }) {
  return <p className="bo-note">{children}</p>
}

/** A dense label/value row — the backoffice's basic unit of information. */
export function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="bo-row">
      <span className="bo-row__label">{label}</span>
      <span className="bo-row__value">{value}</span>
    </div>
  )
}
